import { validateAndSanitiseGBPostcode } from "@./address/validation";

import {
  IAddress,
  IGetAddressResponse,
  TPostcodeCheckResponse,
  AddressValidationState,
  IAddressValidationResult,
  IUserAddress,
} from "./address.types";
import {
  combineAddressLines,
  findBestMatch,
  formatAddress,
  sanitizeAddress,
  sanitizeGBPostcode,
} from "./address.utils";
import { ADDRESS_SIMILARITY_THRESHOLD } from "./address.constants";

export function Address(): IAddress {

  const postcodeCheck = (
    postcode: string,
    skipOutOfBounds = false,
  ): TPostcodeCheckResponse => validateAndSanitiseGBPostcode(postcode, skipOutOfBounds);

  const fetchAddressFromPostcode = async (postcode: string): Promise<IGetAddressResponse> => {
    const sanitizedPostcode = sanitizeGBPostcode(postcode);
    const response = await fetch(`https://api.getAddress.io/find/${sanitizedPostcode}?api-key=dyvE3JfD-kW7Xapg5VQyiw35217&expand=true&sort=true`, {
      method: "GET",
    });
    if (response.ok) return response.json();
    const body = await response.text();
    throw new Error(`Something went wrong: ${response.status}: ${body}`);
  };

  const checkAddressIsValid = async (userAddress: IUserAddress): Promise<IAddressValidationResult> => {
    try {
      const { addresses } = await fetchAddressFromPostcode(userAddress.postcode);
      if (addresses.length === 0) {
        return {
          state: AddressValidationState.NO_MATCH,
        };
      }

      const formattedAddresses = addresses.map((address) => formatAddress(address, userAddress.postcode));

      const combinedUserAddress = [
        userAddress.address1,
        userAddress.address2,
        userAddress.city,
      ].filter(Boolean).join(" ");

      const sanitizedUserAddress = sanitizeAddress(combinedUserAddress);
      const sanitizedUserCity = sanitizeAddress(userAddress.city);

      const exactMatch = formattedAddresses.some((address) => {
        const sanitizedApiAddress = sanitizeAddress(combineAddressLines(address));
        const sanitizedApiCity = sanitizeAddress(address.city);

        return (sanitizedUserAddress === sanitizedApiAddress) && (sanitizedUserCity === sanitizedApiCity);
      });

      if (exactMatch) {
        return { state: AddressValidationState.VALID };
      }

      const { address: bestMatch, similarity } = findBestMatch(sanitizedUserAddress, formattedAddresses);

      if (bestMatch && similarity > ADDRESS_SIMILARITY_THRESHOLD) {
        return {
          state: AddressValidationState.SIMILAR,
          suggestedAddress: bestMatch,
        };
      }

      return {
        state: AddressValidationState.NO_MATCH,
      };

    } catch {
      return {
        state: AddressValidationState.ERROR,
      };
    }
  };

  return {
    fetchAddressFromPostcode,
    postcodeCheck,
    checkAddressIsValid,
  };
}

export default Address;
